import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NoteDialogComponent } from '../../Dialogs/note-dialog/note-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { OrganizationDetailFacade } from '../../../../../Facades/Organizations/organization-detail.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import { NoteDialogResult } from '../../../Interfaces/note-dialog.interface';
import { OrganizationNote } from '../../../../../Models/backoffice/organization-note.model';

@Component({
  selector: 'av-organization-notes',
  standalone: true,
  imports: [DatePipe, MatIconModule, MatTooltipModule, TranslateModule, AvEmptyStateComponent],
  templateUrl: './organization-notes.component.html',
  styleUrl: './organization-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OrganizationNotesComponent {
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(OrganizationDetailFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;

  readonly isEmpty = computed<boolean>(
    () => this.facade.notesState() === 'ready' && this.facade.notes().length === 0,
  );

  openCreate(): void {
    const reference = this._dialog.open(NoteDialogComponent, {
      data: { title: 'backoffice.organizations.notes.dialog.add_title', body: '', pinned: false },
    });

    reference.afterClosed().subscribe((result?: NoteDialogResult) => this._applyCreate(result));
  }

  openEdit(note: OrganizationNote): void {
    const reference = this._dialog.open(NoteDialogComponent, {
      data: { title: 'backoffice.organizations.notes.dialog.edit_title', body: note.body ?? '', pinned: note.pinned },
    });

    reference
      .afterClosed()
      .subscribe((result?: NoteDialogResult) => this._applyEdit(note.id, result));
  }

  togglePinned(note: OrganizationNote): void {
    this.facade.togglePinned(note);
  }

  remove(note: OrganizationNote): void {
    this.facade.deleteNote(note.id);
  }

  canManage(note: OrganizationNote): boolean {
    return this.facade.canManageNote(note);
  }

  private _applyCreate(result?: NoteDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.createNote(result.body, result.pinned);
  }

  private _applyEdit(noteId: string, result?: NoteDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.updateNote(noteId, result.body, result.pinned);
  }
}
